import React from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import { Box, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { Link as LinkGatsby } from "gatsby";
import BlockWrapper from "../core/BlockWrapper";
import { StaticImage } from "gatsby-plugin-image";
import ContainerBlock from "../core/ContainerBlock";

const footerLinks = {
  Quick_Links: [
    {
      name: "What we do",
      href: "/#whatwedo",
      target: "_self",
    },
    {
      name: "How it works",
      href: "/#howitworks",
      target: "_self",
    },
    {
      name: "FAQs",
      href: "/#faqs",
      target: "_self",
    },
    {
      name: "Privacy Policy",
      href: "/privacy-policy",
      target: "_self",
    },
    {
      name: "Terms and conditions",
      href: "/terms-and-condition",
      target: "_self",
    },
  ],
  Get_Started: [
    {
      name: "Request Staff",
      href: process.env.GATSBY_STAFF_REQUEST_FORM_URL as string,
      target: "_blank",
    },
    {
      name: "Join as a Worker",
      href: process.env.GATSBY_JOIN_AS_WORKER_REQUEST_FORM_URL as string,
      target: "_blank",
    },
  ],
};

const contact_details = [
  {
    details: "020 8282 8822 / 07956 8822 48 ",
    logo: <LocalPhoneOutlinedIcon sx={{ color: "#2B2B2B" }} />,
  },
  {
    details: "support@staffmadeeasy.co.uk",
    logo: <EmailOutlinedIcon />,
  },

  {
    details: "922, Uxbridge Road, Hayes, Middlesex, UB4 0RW, UK",
    logo: <LocationOnOutlinedIcon />,
  },
];

const formatSectionTitle = (text: string) =>
  text.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const Footer = () => {
  const theme = useTheme();
  return (
    <BlockWrapper
      sx={{
        background: (theme) => theme.palette.secondary.main,
        paddingBlock: "50px 20px",
      }}
    >
      <ContainerBlock>
        <Grid container spacing={{ xs: 5, md: 8, lg: 20 }} width={"100%"}>
          <Grid
            size={{ xs: 12, md: 6 }}
            display={"flex"}
            flexDirection={"column"}
            gap={"20px"}
            alignItems={"flex-start"}
            pb={"50px"}
          >
            <Box
              component="a"
              href="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                width: { xs: "101px", md: "167px" },
                maxHeight: { xs: "60px", md: "100px" },
                textDecoration: "none",
              }}
            >
              <StaticImage
                src="../../static/logos/logo.png"
                alt="Logo"
                placeholder="blurred"
                loading="lazy"
                style={{
                  width: "100%",
                  height: "auto",
                }}
                imgStyle={{ objectFit: "contain" }}
              />
            </Box>

            <Stack spacing={1}>
              {contact_details.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    color: "#2B2B2B",
                    fontSize: "1rem",
                  }}
                >
                  {/* Icon */}
                  <Box>{item.logo}</Box>

                  {item.logo.type === EmailOutlinedIcon ? (
                    <a
                      href={`mailto:${item.details}`}
                      style={{
                        color: "#2B2B2B",
                        textDecoration: "none",
                      }}
                    >
                      {item.details}
                    </a>
                  ) : item.logo.type === LocalPhoneOutlinedIcon ? (
                    <a
                      href={`tel:${item.details.replace(/\s/g, "")}`}
                      style={{
                        color: "#2B2B2B",
                        textDecoration: "none",
                      }}
                    >
                      {item.details}
                    </a>
                  ) : (
                    <Typography sx={{ color: "#2B2B2B" }}>
                      {item.details}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Grid>
          <Grid
            container
            size={{ xs: 12, md: 6 }}
            spacing={4}
            display={"flex"}
            flexDirection={{ xs: "row-reverse", md: "row" }}
            justifyContent={"space-around"}
          >
            {Object.entries(footerLinks).map(([section, links]) => (
              <Grid
                size={{ xs: 6, md: 4 }}
                key={section}
                display="flex"
                flexDirection="column"
                gap={"16px"}
              >
                <Typography
                  sx={{ fontWeight: 500, fontSize: "16px", color: "#2B2B2B" }}
                >
                  {formatSectionTitle(section)}
                </Typography>

                <Stack spacing={"8px"}>
                  {links.map((link) => (
                    <LinkGatsby
                      title={link.name}
                      key={link.name}
                      to={link.href}
                      target={link.target}
                      partiallyActive={true}
                      activeStyle={{ color: theme.palette.text.secondary }}
                      style={{
                        textDecoration: "none",
                        color: "#2B2B2B",
                        fontSize: "16px",
                        fontWeight: 400,
                      }}
                    >
                      <Box
                        sx={{
                          "&:hover": { color: theme.palette.text.secondary },
                        }}
                      >
                        {link.name}
                      </Box>
                    </LinkGatsby>
                  ))}
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Stack
          direction={{ xs: "column", sm: "column" }}
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          sx={{
            pt: { xs: 4, md: 3 },
            mt: 3,
          }}
          display={"flex"}
          width={"100%"}
          flexDirection={{ xs: "column", md: "row" }}
        >
          <Typography
            sx={{
              color: "#000000",
              fontSize: "16px",
              order: { xs: 3, md: 1 },
              fontWeight: 500,
              textDecoration: "none",
              "& a": {
                color: "#000000",
                fontWeight: 500,
                "&:hover": {
                  color: (theme) => theme.palette.text.secondary,
                },
              },
            }}
          >
            Developed by{" "}
            <a
              href="https://www.xtellig.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Xtellig
            </a>
          </Typography>

          <Typography
            sx={{ color: "#000000", fontSize: "16px", order: { xs: 3, md: 1 } }}
          >
            © 2026 Staff Made Easy. All rights reserved.
          </Typography>
        </Stack>
      </ContainerBlock>
    </BlockWrapper>
  );
};

export default Footer;
